/*
 * Clinch IT — <clinch-price-slider>
 *
 * Dual-handle price slider layered over Dawn's existing min/max text fields.
 * The text fields remain the source of truth: they carry the `name` attributes
 * the facet form serialises, so the slider only ever writes into them. The
 * range inputs have no name and are ignored by FormData.
 *
 * Submission is not triggered here. The range inputs live inside
 * #FacetFiltersForm, so their native `input` event bubbles to the listener
 * FacetFiltersForm binds in its constructor, which debounces by 800ms — one
 * request after the handle settles rather than one per pixel of travel.
 */
(function () {
  if (customElements.get('clinch-price-slider')) return;

  const toNumber = (value) => {
    // Strip the currency group separator so "1,500.00" parses.
    const parsed = parseFloat(String(value == null ? '' : value).replace(/,/g, ''));
    return Number.isNaN(parsed) ? null : parsed;
  };

  class ClinchPriceSlider extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.minSlider = this.querySelector('[data-slider-min]');
      this.maxSlider = this.querySelector('[data-slider-max]');
      this.fill = this.querySelector('[data-slider-fill]');

      // The text fields are siblings inside <price-range>, not children here.
      const scope = this.closest('price-range') || this.parentElement;
      const fields = scope ? scope.querySelectorAll('.clinch-price-fields input[type="text"]') : [];
      this.minInput = fields[0];
      this.maxInput = fields[1];

      if (!this.minSlider || !this.maxSlider || !this.minInput || !this.maxInput) return;

      this.rangeMin = toNumber(this.dataset.min) || 0;
      this.rangeMax = toNumber(this.dataset.max) || 0;
      if (this.rangeMax <= this.rangeMin) return;

      this.minSlider.addEventListener('input', () => this.onSlide('min'));
      this.maxSlider.addEventListener('input', () => this.onSlide('max'));
      this.minInput.addEventListener('input', () => this.onType());
      this.maxInput.addEventListener('input', () => this.onType());

      this.paint();
    }

    /* Handles must not cross each other. */
    onSlide(which) {
      let min = toNumber(this.minSlider.value) || 0;
      let max = toNumber(this.maxSlider.value) || 0;

      if (min > max) {
        if (which === 'min') {
          min = max;
          this.minSlider.value = min;
        } else {
          max = min;
          this.maxSlider.value = max;
        }
      }

      // An untouched end means "no bound" — leave the field empty so the facet
      // URL omits it, matching what Dawn does when the shopper clears a field.
      this.minInput.value = min <= this.rangeMin ? '' : min;
      this.maxInput.value = max >= this.rangeMax ? '' : max;

      this.paint();
    }

    onType() {
      const min = toNumber(this.minInput.value);
      const max = toNumber(this.maxInput.value);

      this.minSlider.value = min == null ? this.rangeMin : min;
      this.maxSlider.value = max == null ? this.rangeMax : max;

      this.paint();
    }

    paint() {
      if (!this.fill) return;

      const span = this.rangeMax - this.rangeMin;
      const min = toNumber(this.minSlider.value) || 0;
      const max = toNumber(this.maxSlider.value) || 0;
      const start = ((min - this.rangeMin) / span) * 100;
      const end = ((max - this.rangeMin) / span) * 100;

      this.fill.style.insetInlineStart = `${start}%`;
      this.fill.style.width = `${Math.max(0, end - start)}%`;
    }
  }

  customElements.define('clinch-price-slider', ClinchPriceSlider);
})();
